import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  getCalendarEventsByRange,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  getStaff,
} from '../api/apiClient';
import socket from '../socket';
import './Calendar.css';

const pad = (v) => String(v).padStart(2, '0');
const formatForServer = (dt) => {
  const d = new Date(dt);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
const toLocalInput = (dt) => {
  const d = new Date(dt);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const isSameDay = (a, b) => {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
};

const emptyForm = { title: '', description: '', location: '', start_date: '', end_date: '' };

const EventPopover = ({ event, pos, onClose, onEdit, onDelete, isAdmin }) => {
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Smart positioning: flip left if near right edge, flip up if near bottom
  const style = { position: 'fixed', zIndex: 9999, top: pos.y, left: pos.x };
  if (pos.x + 300 > window.innerWidth)  style.left  = pos.x - 300;
  if (pos.y + 260 > window.innerHeight) style.top   = pos.y - 260;

  const fmt = dt => new Date(dt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div className="cal-popover" style={style} ref={ref}>
      <button className="cal-popover-close" onClick={onClose}>✕</button>
      <p className="cal-popover-title">{event.title}</p>
      {event.isBirthday ? (
        <p className="cal-popover-time">
          {new Date(event.start).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
      ) : (
        <p className="cal-popover-time">{fmt(event.start)} — {fmt(event.end)}</p>
      )}
      {event.location    && <p className="cal-popover-row">📍 {event.location}</p>}
      {event.description && <p className="cal-popover-desc">{event.description}</p>}
      {event.creatorName && <p className="cal-popover-creator">Added by {event.creatorName}</p>}
      {isAdmin && !event.isBirthday && (
        <div className="cal-popover-actions">
          <button className="cal-edit-btn"   onClick={onEdit}>Edit</button>
          <button className="cal-delete-btn" onClick={onDelete}>Delete</button>
        </div>
      )}
    </div>
  );
};

const Calendar = ({ currentUser }) => {
  const isAdmin = currentUser?.isAdmin;
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add' | 'edit'
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [popoverPos,    setPopoverPos]    = useState({ x: 0, y: 0 });
  const [staff, setStaff] = useState([]);
  const [range, setRange] = useState({ start: null, end: null });

  // Track current visible range for refetching
  const rangeRef = useRef({ start: null, end: null });

  // Load staff birthdays once so we can drop each person's birthday onto the calendar.
  useEffect(() => {
    getStaff().then(r => setStaff((r.data || []).filter(s => s.birthday))).catch(() => {});
    const onStaff = () => getStaff().then(r => setStaff((r.data || []).filter(s => s.birthday))).catch(() => {});
    socket.on('staff:changed', onStaff);
    return () => socket.off('staff:changed', onStaff);
  }, []);

  // Birthdays recur every year — build an all-day event for each staff birthday
  // that falls inside the visible range (handles a range spanning two years).
  const birthdayEvents = useMemo(() => {
    if (!range.start || !range.end) return [];
    const out = [];
    for (const s of staff) {
      const mm = parseInt((s.birthday || '').slice(0, 2), 10);
      const dd = parseInt((s.birthday || '').slice(3, 5), 10);
      if (!mm || !dd) continue;
      for (let y = range.start.getFullYear(); y <= range.end.getFullYear(); y++) {
        const occ = new Date(y, mm - 1, dd);
        if (occ >= range.start && occ < range.end) {
          out.push({
            id: `bday-${s.id}-${y}`,
            title: `🎂 ${s.name}`,
            start: `${y}-${pad(mm)}-${pad(dd)}`,
            allDay: true,
            color: '#db2777',
            extendedProps: { isBirthday: true, personName: s.name },
          });
        }
      }
    }
    return out;
  }, [staff, range]);

  const allEvents = useMemo(() => [...events, ...birthdayEvents], [events, birthdayEvents]);

  const fetchEvents = useCallback(async (start, end) => {
    try {
      const response = await getCalendarEventsByRange(
        formatForServer(start),
        formatForServer(end)
      );
      const mapped = response.data.map((e) => ({
        id: String(e.id),
        title: e.title,
        start: e.start_date.replace(' ', 'T'),
        end: e.end_date.replace(' ', 'T'),
        extendedProps: {
          description: e.description || '',
          location: e.location || '',
          createdBy: e.created_by,
          creatorName: e.creator_name || '',
        },
      }));
      setEvents(mapped);
    } catch (err) {
      console.error('Error fetching calendar events:', err);
    }
  }, []);

  const refetch = useCallback(() => {
    if (rangeRef.current.start && rangeRef.current.end) {
      fetchEvents(rangeRef.current.start, rangeRef.current.end);
    }
  }, [fetchEvents]);

  useEffect(() => {
    socket.on('calendar:changed', refetch);
    return () => socket.off('calendar:changed', refetch);
  }, [refetch]);

  const handleDatesSet = useCallback((info) => {
    rangeRef.current = { start: info.start, end: info.end };
    setRange({ start: info.start, end: info.end });
    fetchEvents(info.start, info.end);
  }, [fetchEvents]);

  // Today's events for "Upcoming Events" section
  const todayEvents = events.filter((e) => isSameDay(e.start, new Date()));

  const handleDateClick = (info) => {
    if (!isAdmin) return;
    const clickedDate = info.dateStr;
    const start = clickedDate.includes('T') ? clickedDate : `${clickedDate}T09:00`;
    const endDate = new Date(start);
    endDate.setHours(endDate.getHours() + 1);
    setFormData({
      ...emptyForm,
      start_date: toLocalInput(start),
      end_date: toLocalInput(endDate),
    });
    setFormMode('add');
    setEditingId(null);
    setSelectedEvent(null);
    setShowForm(true);
  };

  const handleEventClick = (info) => {
    const ev = info.event;
    const rect = info.el.getBoundingClientRect();
    setPopoverPos({ x: rect.left, y: rect.bottom + 6 });
    if (ev.extendedProps.isBirthday) {
      setSelectedEvent({
        id: ev.id,
        title: `${ev.extendedProps.personName}'s Birthday 🎂`,
        start: ev.start,
        isBirthday: true,
      });
      setShowForm(false);
      return;
    }
    setSelectedEvent({
      id: ev.id,
      title: ev.title,
      start: ev.start,
      end: ev.end,
      description: ev.extendedProps.description,
      location: ev.extendedProps.location,
      creatorName: ev.extendedProps.creatorName,
    });
    setShowForm(false);
  };

  const handleEdit = () => {
    if (!selectedEvent) return;
    setFormData({
      title: selectedEvent.title,
      description: selectedEvent.description || '',
      location: selectedEvent.location || '',
      start_date: toLocalInput(selectedEvent.start),
      end_date: toLocalInput(selectedEvent.end),
    });
    setFormMode('edit');
    setEditingId(selectedEvent.id);
    setShowForm(true);
    setSelectedEvent(null);
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    try {
      await deleteCalendarEvent(selectedEvent.id, { data: { is_admin: true } });
      setSelectedEvent(null);
      refetch();
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.start_date || !formData.end_date) return;

    const payload = {
      title: formData.title,
      description: formData.description,
      location: formData.location,
      start_date: formData.start_date.replace('T', ' '),
      end_date: formData.end_date.replace('T', ' '),
    };

    try {
      if (formMode === 'edit' && editingId) {
        await updateCalendarEvent(editingId, { ...payload, is_admin: true });
      } else {
        await createCalendarEvent({ ...payload, created_by: currentUser.id, is_admin: true });
      }
      setShowForm(false);
      setFormData(emptyForm);
      setEditingId(null);
      refetch();
    } catch (err) {
      console.error('Error saving event:', err);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData(emptyForm);
    setEditingId(null);
    setSelectedEvent(null);
  };

  return (
    <div className="calendar-section">
      <div className="calendar-top-bar">
        <h2>Calendar</h2>
        {isAdmin && !showForm && (
          <button
            className="cal-add-btn"
            onClick={() => {
              setFormData(emptyForm);
              setFormMode('add');
              setEditingId(null);
              setSelectedEvent(null);
              setShowForm(true);
            }}
          >
            + Add Event
          </button>
        )}
      </div>

      <div className="fullcalendar-wrapper">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          events={allEvents}
          datesSet={handleDatesSet}
          dateClick={isAdmin ? handleDateClick : undefined}
          eventClick={handleEventClick}
          height="auto"
          nowIndicator={true}
          dayMaxEvents={3}
          eventDisplay="block"
          eventColor="#1a237e"
          slotMinTime="07:00:00"
          slotMaxTime="20:00:00"
          scrollTime="08:00:00"
          slotDuration="00:30:00"
          eventContent={(arg) => {
            const location = arg.event.extendedProps.location;
            const label = location ? `${arg.event.title} @ ${location}` : arg.event.title;
            return { html: `<div class="fc-event-label">${label}</div>` };
          }}
        />
      </div>

      {selectedEvent && (
        <EventPopover
          event={selectedEvent}
          pos={popoverPos}
          onClose={() => setSelectedEvent(null)}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isAdmin={isAdmin}
        />
      )}

      {/* Add / Edit event form */}
      {showForm && isAdmin && (
        <div className="event-form-card">
          <h3>{formMode === 'edit' ? 'Edit Event' : 'Add Event'}</h3>
          <form onSubmit={handleFormSubmit} className="cal-event-form">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Event title"
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start</label>
                <input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>End</label>
                <input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Location (optional)"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description (optional)"
                rows="3"
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="cal-save-btn">
                {formMode === 'edit' ? 'Save Changes' : 'Create Event'}
              </button>
              <button type="button" className="cal-cancel-btn" onClick={handleCancel}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Upcoming Events — today only */}
      <div className="upcoming-events">
        <h3>Upcoming Events — Today</h3>
        {todayEvents.length === 0 ? (
          <p className="no-events-today">No events scheduled for today.</p>
        ) : (
          <div className="today-events-list">
            {todayEvents.map((ev) => (
              <div key={ev.id} className="today-event-card">
                <div className="today-event-time">
                  {new Date(ev.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {' - '}
                  {new Date(ev.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div className="today-event-info">
                  <h4>{ev.title}</h4>
                  {ev.extendedProps.location && (
                    <span className="today-event-location">📍 {ev.extendedProps.location}</span>
                  )}
                  {ev.extendedProps.description && (
                    <p className="today-event-desc">{ev.extendedProps.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Calendar;
